import {Relation} from "@traas/lib/src/api/dto/Relation";
import React, {FC, useMemo, useState} from "react";
import {VatRate} from "@traas/lib/src/api/dto/VatRate";
import {Ledger} from "@traas/lib/src/api/dto/Ledger";
import {useNavigate} from "react-router";
import {useApiService} from "@traas/lib/src/api/hooks/useApiService";
import config from "../config";
import {useApiResourceContext} from "@traas/lib/src/api-resource-context/useApiResourceContext";
import {ErrorBag, ValidationError} from "../api/api";
import {Button, getToken, IconButton} from "@ventumsoftware/react-toolkit";
import {ModalFooter, useModal} from "@traas/lib/src/components/layout/ModalProvider";
import {AddRelationModal} from "./Relations/AddRelationModal";
import {InputsErrors} from "@traas/lib/src/components/form/InputErrors";
import {faCircleMinus, faPlus, faSave} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {Autocomplete} from "@traas/lib/src/components/form/Autocomplete";
import {Input} from "../../../lib/src/components/form/Input";
import {Task} from "@traas/lib/src/api/dto/Task";
import {Order, OrderStatus, orderStatusOptions} from "@traas/lib/src/api/dto/Order";
import {Select} from "../../../lib/src/components/form/Select";

export const EditOrderModal: FC<{ order: Order, task: Task, relations: Relation[], vatRates: VatRate[], ledgers: Ledger[], onFinish?: (order: Order) => void}> = (props) => {
  const navigate = useNavigate()
  const { orderService } = useApiService(config, getToken, navigate)
  const {contacts} = useApiResourceContext()
  const [errors, setErrors] = useState<ErrorBag>({})

  const [amount, setAmount] = useState<number>(props.order.amount ?? 0)
  const [price, setPrice] = useState<number>((props.order.price ?? 0) / 100)
  const [requestDate, setRequestDate] = useState<Date|null>(props.order.requestDate ?? new Date())
  const [deliveryDate, setDeliveryDate] = useState<Date|null>(props.order.deliveryDate ?? new Date())
  const [vatRate, setVatRate] = useState<string>(props.order.vatRateId ?? '')
  const [ledgerCode, setLedgerCode] = useState<string>(props.order.ledgerCodeId ?? '')
  const [description, setDescription] = useState<string>(props.order.description ?? '')
  const [remark, setRemark] = useState<string>(props.order.remark ?? '')
  const [status, setStatus] = useState<OrderStatus>(props.order.status ?? 'concept')
  const [relationId, setRelationId] = useState<string>(props.order.supplierRelationId ?? '-')
  const [contactId, setContactId] = useState<string>(props.order.supplierContactId ?? '-')

  const ledgerMap = props.ledgers.filter(l => l.type === 'credit').map((ledger) => [ledger.id, `${ledger.code } - ${ledger.description}`])
  const vatRateMap = props.vatRates.map((rate) => [rate.id, `${rate.name } (${rate.rate}%)`])

  const relationOptions: [string, string, string][] = useMemo(() => {
    return [
      ['-', 'Selecteer relatie', ''], ...props.relations
        .filter(r => r.isSupplier)
        .map<[string, string, string]>(item => {
          const additionalSearchKeywords = [
            item.email ?? '',
            item.phone ?? '',
            item.locationCity ?? '',
          ].join(' ')
          return [item.id, item.name ?? '??', additionalSearchKeywords];
        })
    ]
  }, [props.relations]);

  const contactOptions: [string, string, string][] = useMemo(() => {
    return [
      ['-', 'Selecteer contact', ''], ...contacts
        .filter(item => item.relationId === relationId)
        .map<[string, string, string]>(item => {
          const additionalSearchKeywords = [
            item.email ?? '',
            item.phone ?? '',
            item.office_phone ?? '',
          ].join(' ')
          return [item.id, item.full_name ?? '??', additionalSearchKeywords];
        })
    ]
  }, [contacts, relationId]);

  const relation = props.relations.find(item => item.id === relationId)
  const addRelationModal = useModal({title: 'Relatie toevoegen', body: <AddRelationModal vatRates={props.vatRates} ledgerAccounts={props.ledgers} onFinish={(relation) => {
      setRelationId(relation.id)
    }} />})
  // const addContactModal = useModal({title: 'Contactpersoon toevoegen', body: <AddContactModal relation={relation!} onFinish={(contact) => {
  //     setContactId(contact.id)
  //   }} />})

  const save = async () => {
    await orderService.editOrder(props.order.id, relationId, contactId, props.order.projectId, props.order.quoteId, amount, price * 100, requestDate ?? new Date(), deliveryDate, vatRate, ledgerCode, description, remark, status)
      .catch(e => {
        if (e instanceof ValidationError) {
          setErrors(e.errors)
        }
        throw e
      })
  }


  return <>
    <div className={'grid grid-cols-2 gap-4'}>
      <div className={'flex items-end space-x-2'}>
        <Autocomplete label={'Relatie'} options={relationOptions} value={relationId} onChange={(x) => {
          setRelationId(x)
          const relation = props.relations.find(item => item.id === x)
          if (relation?.vatRateId) {
            setVatRate(relation.vatRateId)
          }
          if (relation?.supplierDefaultLedgerAccountId) {
            setLedgerCode(relation.supplierDefaultLedgerAccountId)
          }
        }} />
        <IconButton type={'secondary'} icon={faPlus} size={'md'} onClick={() => {
          addRelationModal.open()
        }} />
      </div>

      <div className={'flex items-end space-x-2'}>
        <Autocomplete label={'Contactpersoon'} options={contactOptions} value={contactId} onChange={setContactId} />
        {/*<IconButton type={'secondary'} disabled={!relationId || relationId === '-'} icon={faPlus} size={'md'} onClick={() => {*/}
        {/*  addContactModal.open()*/}
        {/*}} />*/}
      </div>
      <Select label={'Status'} options={orderStatusOptions} value={status} onChange={(x) => setStatus(x as OrderStatus)} />


      <Autocomplete label={'BTW tarief'} options={Object.entries(Object.fromEntries(vatRateMap))} value={vatRate} onChange={setVatRate} />
      <Autocomplete label={'Rekening'} options={Object.entries(Object.fromEntries(ledgerMap))} value={ledgerCode} onChange={setLedgerCode} />

      <div className={'grid grid-cols-2 gap-3'}>
        <Input type={'number'} label={'Aantal'} value={amount} onChange={setAmount} />
        <Input type={'price'} label={'Prijs'} value={price} onChange={setPrice} />
      </div>

      <div className={'col-span-2'}>
        <Input type={'textarea'} label={"Omschrijving"} value={description} onChange={setDescription} />
      </div>

      <div className={'col-span-2'}>
        <Input type={'textarea'} label={"Lever-opmerking"} value={remark} onChange={setRemark} />
      </div>

      <div>
        <Input type={'date'} label={'Bestel datum'} value={requestDate!} onChange={(d) => {
          setRequestDate(d)
        }} />
      </div>

      {deliveryDate !== null ? <div className={"col-span-1"}>
          <div className={'flex space-x-2 items-center'}>
            <Input type={'date'} label={'Lever datum'} value={deliveryDate} onChange={setDeliveryDate}/>
            <FontAwesomeIcon className={'mt-6 px-3 py-3 text-red-600 hover:text-red-200 hover:bg-red-600 hover:cursor-pointer rounded-sm'} icon={faCircleMinus} onClick={() => {
              setDeliveryDate(null)
            }}/>
          </div>
        </div> :
        <div className={'flex justify-center items-center -mb-4'}>
          <Button type={'secondary'} size={'sm'} text={'Leverdatum'} icon={faPlus} onClick={() => {
            setRequestDate(new Date())
          }}/>
        </div>}
      <div></div>
    </div>
    <InputsErrors errors={errors} fields={['supplier_relation_id', 'supplier_contact_id', 'vat_rate_id', 'ledger_code_id']} />
    <ModalFooter
      text={'Opslaan'}
      icon={faSave}
      onSubmit={save}
    />
  </>
}

// import {FC, useState} from "react";
// import {ModalFooter} from "@traas/lib/src/components/layout/ModalProvider";
// import {faSave} from "@fortawesome/free-solid-svg-icons";
// import {Input} from "@ventumsoftware/react-toolkit";
//
// export const AddOrderModal: FC<{projectId: string, relationId: string, }> = () => {
//   const [amount, setAmount] = useState<number>(0)
//   const [price, setPrice] = useState<number>(0)
//   const [description, setDescription] = useState<string>('')
//
//   return <div>
//     <div className={'flex flex-col space-y-4'}>
//       <div className={'flex flex-row space-x-4'}>
//         <Input type={'number'} label={'hoeveelheid'} value={amount} onChange={setAmount} />
//         <Input type={'number'} label={'Prijs'} value={price} onChange={setPrice} />
//       </div>
//       <Input type={'textarea'} label={'Beschrijving'} value={description} onChange={setDescription}/>
//     </div>
//     <ModalFooter text={'Opslaan'} icon={faSave} onSubmit={() => {}} />
//   </div>
// }

